/**
 * 55 — screen 4: THE WORKBENCH. Jonathan's door.
 *
 * Hidden behind the `⋯` on the song screen, visible only to role='admin'.
 * Everything ugly and powerful lives in here so the other three screens can
 * stay stupid-simple. Nobody else in the band will ever see this, and that is
 * the entire point of it existing.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  THE CENTRAL PRINCIPLE (ADR-005): THIS IS A CUT-POINT EDITOR.
 *
 *  It never modifies an audio byte. It never decodes one. It draws a waveform
 *  from peaks that `audiowaveform` computed on the Mac, you drag some boxes,
 *  and it POSTs FOUR NUMBERS PER BOX. The Roadie then runs `ffmpeg -c copy` —
 *  a stream copy: no re-encode, no quality loss, milliseconds.
 *
 *  ffmpeg.wasm would be 31 MB, GPL-2 at the core, and would OOM a cheap
 *  Android on a 20-minute jam. Sending four numbers instead of 200 MB is not a
 *  compromise. It is simply the better design.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * And the second principle, ADR-010: NOTHING IS EVER DELETED. Every edit here
 * makes a NEW row. Split a jam and the jam is still there. Trim four minutes
 * of tuning-up off the front and the tuning-up is still there — which matters,
 * because a third of the time the good riff was in the tuning-up.
 *
 * Wiring: the orchestrator mounts this and listens for `workbench:close`,
 * which bubbles out of the root when the back arrow is pressed at the top
 * level. Nothing in here knows about routing.
 */

import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions.js';

import {
  ApiError,
  confirmTake,
  getCandidates,
  getPeaks,
  getSong,
  getSongs,
  getTakeStream,
  getUnsortedTakes,
  mergeSong,
  patchSong,
  patchTake,
  promoteTake,
  splitTake,
  trimTake,
  type Candidate,
  type Segment,
  type Song,
  type SongRef,
  type Take,
  type TakeKind,
} from './api';
import * as player from './player';
import {
  clear,
  emptyState,
  fmtTime,
  h,
  icon,
  promptSheet,
  relDate,
  sheet,
  skeleton,
  toast,
  type SheetItem,
} from './ui';

/* ------------------------------------------------------------------- kinds */

/**
 * The five things that can be on a tape.
 *
 * Naming them honestly is what turns a 20-minute file into a library: the
 * false starts and the argument about pizza are not rubbish to be thrown away.
 * They are labelled, kept, and hidden. (Nothing is ever deleted — but plenty
 * is quietly filed.)
 */
const KINDS: { id: TakeKind; label: string; color: string; hint: string }[] = [
  { id: 'full', label: 'The song', color: '#ff3333', hint: 'a proper run at it' },
  { id: 'fragment', label: 'A bit of it', color: '#ff9922', hint: 'part of the song' },
  { id: 'false_start', label: 'False start', color: '#8a8a8a', hint: 'died after four bars' },
  { id: 'talking', label: 'Talking', color: '#3388ff', hint: 'the band, arguing' },
  { id: 'noodle', label: 'Noodling', color: '#cc44ff', hint: 'might be a new song' },
];

const kindOf = (k: TakeKind) => KINDS.find((x) => x.id === k) ?? KINDS[0]!;
const regionColor = (k: TakeKind, alpha = 0.22) => hexA(kindOf(k).color, alpha);

function hexA(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/* ------------------------------------------------------------------- state */

/** 'idle' means no take is open. A take is always open in one of the others. */
type Mode = 'idle' | 'split' | 'trim' | 'confirm';

/** A proposed cut: a box on the waveform and a row in the list, same object. */
interface Cut {
  id: string;
  region: Region;
  kind: TakeKind;
  songId: string | null;
  label: string | null;
}

interface State {
  song: Song | null;
  takes: Take[];
  unsorted: Take[];
  songs: SongRef[];

  take: Take | null;
  peaks: number[][] | null;
  duration: number;

  mode: Mode;
  cuts: Cut[];
  candidates: Candidate[];

  loading: boolean;
  busy: boolean;
  error: string | null;
}

/* ------------------------------------------------------------------- mount */

export function mountWorkbench(root: HTMLElement, songId: string): () => void {
  injectCss();

  const state: State = {
    song: null,
    takes: [],
    unsorted: [],
    songs: [],
    take: null,
    peaks: null,
    duration: 0,
    mode: 'idle',
    cuts: [],
    candidates: [],
    loading: true,
    busy: false,
    error: null,
  };

  let ws: WaveSurfer | null = null;
  let regions: RegionsPlugin | null = null;
  let dead = false;
  const abort = new AbortController();

  /**
   * The workbench BORROWS the app's one <audio> element. It does not make a
   * second one: two media elements on iOS fight over the audio session and the
   * loser goes silent with no error and no way to diagnose it. We take it here
   * and hand it back, cleaned, on unmount.
   */
  const audio = player.element();
  player.stop();

  const view = h('div', { class: 'wb' });
  root.appendChild(view);

  /**
   * The waveform's home, created ONCE and re-attached on every render.
   *
   * This matters more than it looks. wavesurfer owns real DOM (and a shadow
   * root) inside this node. A render() that rebuilt it would throw wavesurfer's
   * canvas away and leave the instance pointing at a detached element — a
   * waveform that silently vanishes the first time you change a dropdown. So
   * the host is a stable object; render() moves it, and never re-creates it.
   */
  const waveHost = h('div', { class: 'wb-wave' });
  const waveWrap = h('div', { class: 'wb-wave-wrap' }, [waveHost]);

  /* ------------------------------------------------------------ data loads */

  async function loadSong(): Promise<void> {
    state.loading = true;
    render();
    try {
      const [detail, songs, unsorted] = await Promise.all([
        getSong(songId, abort.signal),
        getSongs(abort.signal),
        // The "help me name these" pile is the least important thing on screen.
        // It must never be the reason the whole workbench fails to open.
        getUnsortedTakes(songId, abort.signal).catch(() => [] as Take[]),
      ]);
      if (dead) return;

      state.song = detail.song;
      state.takes = detail.takes;
      state.songs = songs.map((s) => ({ id: s.id, title: s.title, color: s.color ?? null }));
      state.unsorted = unsorted;
      state.error = null;
    } catch (e) {
      if (dead) return;
      state.error = e instanceof ApiError ? e.human : 'Couldn’t reach the vault. Try again?';
    } finally {
      state.loading = false;
      render();
    }
  }

  /* -------------------------------------------------------- opening a take */

  async function openTake(take: Take, mode: Exclude<Mode, 'idle'>): Promise<void> {
    teardownWave();

    state.take = take;
    state.mode = mode;
    state.cuts = [];
    state.candidates = [];
    state.peaks = null;
    state.duration = take.duration_s;
    state.loading = true;
    render();

    try {
      const info = await getTakeStream(take.id, abort.signal);
      if (dead) return;

      state.duration = info.duration_s || take.duration_s;

      /* ═══ THE LINE THAT KEEPS THE PHONE ALIVE ═══
       *
       * Peaks are pre-computed by `audiowaveform` on the Mac and fetched from
       * R2 as JSON. We never hand wavesurfer a bare URL and let it fetch and
       * decode: a 20-minute file is a 30-second stall on a good laptop and an
       * OOM crash on a cheap Android.
       *
       * No peaks → no waveform → no cutting. That is the correct answer, and
       * the screen says so in a sentence instead of hanging. */
      state.peaks = info.peaks_url ? await getPeaks(info.peaks_url, abort.signal) : null;
      if (dead) return;

      if (mode === 'confirm') {
        const { candidates, songs } = await getCandidates(take.id, abort.signal);
        if (dead) return;
        state.candidates = candidates;
        if (songs.length) state.songs = songs;
      }

      state.loading = false;
      render();

      if (state.peaks && mode !== 'confirm') mountWave(info.url);
    } catch (e) {
      if (dead) return;
      state.loading = false;
      state.error = e instanceof ApiError ? e.human : 'Couldn’t open that one. Try again?';
      render();
      toast(state.error, true);
    }
  }

  /**
   * wavesurfer, over the SHARED <audio> element.
   *
   *   media    → playback goes through the app's one media element, not Web
   *              Audio. (Web Audio is muted by the iOS silent switch. Fatal.)
   *   peaks    → pre-computed. Nothing is fetched, nothing is decoded.
   *   duration → given, so it never waits on metadata to draw.
   *   url      → wavesurfer just sets media.src to it and stops there.
   *
   * With `peaks` AND `duration` both set, wavesurfer's loadAudio() skips the
   * fetch and the decode entirely. That's not a happy accident — it's the
   * documented path, and it's the only reason this screen works on a phone.
   */
  function mountWave(url: string): void {
    if (!state.peaks || ws) return;

    regions = RegionsPlugin.create();

    ws = WaveSurfer.create({
      container: waveHost,
      media: audio,
      peaks: state.peaks,
      duration: state.duration,
      url,
      height: window.innerWidth < 520 ? 96 : 132,
      waveColor: 'rgba(255,255,255,0.26)',
      progressColor: 'rgba(255,255,255,0.52)',
      cursorColor: '#ff3333',
      cursorWidth: 2,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      normalize: true,
      dragToSeek: true,
      minPxPerSec: 1,
      plugins: [regions],
    });

    const paintPlay = () => {
      const btn = view.querySelector<HTMLElement>('.wb-play');
      if (btn) btn.innerHTML = ws?.isPlaying() ? icon.pause : icon.play;
    };
    ws.on('play', paintPlay);
    ws.on('pause', paintPlay);

    regions.on('region-updated', (r) => {
      const cut = state.cuts.find((c) => c.region === r);
      if (cut) paintRow(cut);
    });

    // Tap a box, hear exactly that box, stop at the end of it. This is the
    // entire verification loop — drag, tap, listen, adjust — so it has to be
    // instant and it has to be one finger.
    regions.on('region-clicked', (r, e) => {
      e.stopPropagation();
      r.play(true);
    });

    ws.on('ready', () => {
      if (dead) return;
      if (state.mode === 'split' && state.cuts.length === 0) proposeCuts();
      if (state.mode === 'trim' && state.cuts.length === 0) seedTrim();
      render();
    });
  }

  function teardownWave(): void {
    // Destroying a wavesurfer whose media is EXTERNAL does not touch the media
    // element — it unsubscribes and leaves. The <audio> belongs to the app.
    try {
      ws?.destroy();
    } catch {
      /* a half-initialised wavesurfer throws on destroy. Nothing to do about it. */
    }
    ws = null;
    regions = null;
    clear(waveHost);
    audio.pause();
  }

  /* ---------------------------------------------- proposing the cut points */

  /**
   * ═══ THE ROADIE PROPOSES; YOU DISPOSE ═══
   *
   * The boundaries below are found in the PEAKS ARRAY — the same numbers we
   * are already drawing, already in memory. A run of quiet longer than a few
   * seconds, in a room with five people in it, is a gap between songs.
   *
   * That is not a clever algorithm and it is not trying to be. It is a first
   * guess whose only job is to be close enough that you DRAG instead of DRAW.
   *
   * DESIGN FOR ONE TAP, NOT FOR A PERFECT AUTO-SPLITTER. An auto-splitter
   * that's right 70% of the time and can't be corrected is worse than useless.
   * A rough guess you fix with four drags turns a 40-minute chore into a
   * 40-second one, and it never has to be cleverer than that.
   *
   * (When `roadie segment` lands — inaSpeechSegmenter, 05_INGEST_PIPELINE
   * Stage 1 — it writes child takes directly and they show up in the takes
   * list. This is the browser-side stopgap, and it costs nothing: no network,
   * no decode, no CPU worth measuring.)
   */
  function proposeCuts(): void {
    if (!state.peaks || !state.duration) return;

    for (const b of boundaries(state.peaks, state.duration)) addCut(b.start, b.end, b.kind);

    // All silence, or all noise. Either way: one box, the whole thing, drag it.
    if (state.cuts.length === 0) addCut(0, state.duration, 'full');

    render();
  }

  function seedTrim(): void {
    if (!state.duration) return;
    // Start the box where the sound starts. Nine times in ten the thing you
    // came here to cut is the silence and the tuning-up at the front.
    const found = state.peaks ? sounding(state.peaks, state.duration) : null;
    addCut(found?.start ?? 0, found?.end ?? state.duration, 'full');
    render();
  }

  function addCut(start: number, end: number, kind: TakeKind): void {
    if (!regions) return;

    const id = crypto.randomUUID();
    const region = regions.addRegion({
      id,
      start: Math.max(0, start),
      end: Math.min(state.duration, Math.max(start + 1, end)),
      color: regionColor(kind),
      drag: true,
      resize: true,
      minLength: 1,
    });

    state.cuts.push({ id, region, kind, songId: state.take?.song_id ?? null, label: null });
  }

  function dropCut(cut: Cut): void {
    cut.region.remove();
    state.cuts = state.cuts.filter((c) => c !== cut);
    render();
  }

  /* ------------------------------------------------------------- the writes */

  /** Every write goes through here: one place to be busy, one place to fail. */
  async function commit(what: () => Promise<void>, done: string): Promise<void> {
    if (state.busy) return;
    state.busy = true;
    render();

    try {
      await what();
      if (dead) return;

      toast(done);
      teardownWave();
      state.take = null;
      state.mode = 'idle';
      state.cuts = [];
      await loadSong();
    } catch (e) {
      if (dead) return;
      toast(e instanceof ApiError ? e.human : 'That didn’t take. Try again?', true);
    } finally {
      state.busy = false;
      if (!dead) render();
    }
  }

  function doSplit(): void {
    const take = state.take;
    if (!take || state.cuts.length === 0) return;

    const segments: Segment[] = [...state.cuts]
      .sort((a, b) => a.region.start - b.region.start)
      .map((c) => ({
        start_s: round(c.region.start),
        end_s: round(c.region.end),
        song_id: c.songId,
        kind: c.kind,
        label: c.label,
      }));

    const named = segments.filter((s) => s.song_id).length;

    void commit(
      () => splitTake(take.id, segments).then(() => undefined),
      named
        ? `Cut into ${segments.length}. ${named} named — the matcher just got better.`
        : `Cut into ${segments.length}. Your Mac will slice them next time it's awake.`,
    );
  }

  function doTrim(): void {
    const take = state.take;
    const cut = state.cuts[0];
    if (!take || !cut) return;

    void commit(
      () =>
        trimTake(take.id, round(cut.region.start), round(cut.region.end), cut.label).then(
          () => undefined,
        ),
      'Trimmed. The original is untouched — it always is.',
    );
  }

  function doConfirm(songId: string): void {
    const take = state.take;
    if (!take) return;
    void commit(() => confirmTake(take.id, songId).then(() => undefined), 'Got it. That makes the next guess better.');
  }

  async function doPromote(take: Take): Promise<void> {
    const suggested =
      take.session_label || (take.recorded_at ? relDate(take.recorded_at) : '') || 'A version';

    const name = await promptSheet({
      title: 'Save this as a version',
      label: 'Call it something the band will recognise',
      value: suggested,
      placeholder: 'Apr 4 full band',
      confirm: 'Save it',
    });
    if (!name) return;

    void commit(() => promoteTake(take.id, name).then(() => undefined), `Saved as “${name}”.`);
  }

  async function doRenameTake(take: Take): Promise<void> {
    const label = await promptSheet({
      title: 'What is this bit?',
      label: 'A name for it',
      value: take.label ?? '',
      placeholder: 'the ending / breakdown idea',
      confirm: 'Save',
    });
    if (!label) return;

    void commit(() => patchTake(take.id, { label }), 'Renamed.');
  }

  async function doRenameSong(): Promise<void> {
    const song = state.song;
    if (!song) return;

    const title = await promptSheet({
      title: 'Rename the song',
      label: 'Title',
      value: song.title,
      confirm: 'Save',
    });
    if (!title || title === song.title) return;

    void commit(() => patchSong(song.id, { title }), 'Renamed.');
  }

  function doMerge(): void {
    const song = state.song;
    if (!song) return;

    const others = state.songs.filter((s) => s.id !== song.id);
    if (others.length === 0) {
      toast('There’s nothing to merge it into.', true);
      return;
    }

    sheet(`Merge “${song.title}” into…`, [
      ...others.map((o) => ({
        label: o.title,
        onSelect: () =>
          void commit(
            () => mergeSong(song.id, o.id),
            `Merged into “${o.title}”. The old name is kept as an alias — the matcher still knows it.`,
          ),
      })),
    ]);
  }

  function pickSongFor(cut: Cut): void {
    sheet('Which song is this bit?', [
      {
        label: 'Don’t know yet',
        onSelect: () => {
          cut.songId = null;
          paintRow(cut);
        },
      },
      ...state.songs.map((s) => ({
        label: s.title,
        onSelect: () => {
          cut.songId = s.id;
          paintRow(cut);
        },
      })),
    ]);
  }

  function pickKindFor(cut: Cut): void {
    sheet('What is this bit?', [
      ...KINDS.map((k) => ({
        label: `${k.label} — ${k.hint}`,
        onSelect: () => {
          cut.kind = k.id;
          cut.region.setOptions({ color: regionColor(k.id) });
          paintRow(cut);
        },
      })),
    ]);
  }

  /* ------------------------------------------------------------------ render */

  /**
   * Cut rows are PATCHED IN PLACE, never re-rendered. Rebuilding the DOM under
   * a finger that is halfway through a drag is exactly how an editor comes to
   * feel broken. Everything else is a full redraw, because it's forty nodes.
   */
  function paintRow(cut: Cut): void {
    const row = view.querySelector<HTMLElement>(`[data-cut="${cut.id}"]`);
    if (!row) return;

    const k = kindOf(cut.kind);
    const song = state.songs.find((s) => s.id === cut.songId);

    row.style.setProperty('--k', k.color);
    row.dataset.unnamed = song ? '0' : '1';

    const set = (sel: string, text: string) => {
      const el = row.querySelector(sel);
      if (el) el.textContent = text;
    };
    set('.cut-time', `${fmtTime(cut.region.start)} – ${fmtTime(cut.region.end)}`);
    set('.cut-dur', fmtTime(cut.region.end - cut.region.start));
    set('.cut-kind', k.label);
    set('.cut-song', song ? song.title : 'Which song?');
  }

  function render(): void {
    if (dead) return;
    clear(view);

    view.appendChild(header());

    if (state.loading && !state.song) {
      view.appendChild(skeleton(2));
      return;
    }
    if (state.error && !state.song) {
      view.appendChild(emptyState('Couldn’t open the workbench', state.error));
      return;
    }

    if (state.take) {
      view.appendChild(editor());
      return;
    }

    view.appendChild(songBlock());
    view.appendChild(takesBlock());
    view.appendChild(unsortedBlock());
  }

  function header(): HTMLElement {
    const title = state.take
      ? state.take.label || state.take.filename || 'This take'
      : (state.song?.title ?? 'The workbench');

    const back = h('button', { class: 'wb-x', 'aria-label': 'Back', html: icon.back });
    back.addEventListener('click', () => {
      if (state.take) {
        teardownWave();
        state.take = null;
        state.mode = 'idle';
        state.cuts = [];
        render();
      } else {
        // The orchestrator owns routing. We just say that we're done.
        view.dispatchEvent(new CustomEvent('workbench:close', { bubbles: true }));
      }
    });

    return h('div', { class: 'wb-head' }, [
      back,
      h('div', { class: 'wb-title' }, [
        h('h1', { text: title }),
        h('p', {
          text: state.take
            ? 'Nothing here changes the original. Every edit makes a new take.'
            : 'The ugly, powerful door.',
        }),
      ]),
      state.busy ? h('span', { class: 'wb-busy', 'aria-label': 'Working' }) : null,
    ]);
  }

  /* ---- the song itself ---------------------------------------------------- */

  function songBlock(): HTMLElement {
    const song = state.song!;

    const rename = h('button', { class: 'wb-btn' }, [
      h('span', { class: 'lead', html: icon.pencil }),
      h('span', { text: 'Rename this song' }),
    ]);
    rename.addEventListener('click', () => void doRenameSong());

    const merge = h('button', { class: 'wb-btn' }, [
      h('span', { class: 'lead', html: icon.archive }),
      h('span', { text: 'Merge into another song' }),
    ]);
    merge.addEventListener('click', doMerge);

    return h('section', { class: 'wb-card' }, [
      h('h2', { text: 'The song' }),
      h('div', { class: 'wb-facts' }, [
        fact('Takes', String(song.take_count)),
        fact('Versions', String(song.version_count)),
        fact('The one', song.canonical ? song.canonical.name : 'Nobody’s picked it'),
      ]),
      rename,
      merge,
    ]);
  }

  const fact = (k: string, v: string) =>
    h('div', { class: 'wb-fact' }, [h('span', { text: k }), h('strong', { text: v })]);

  /* ---- the takes of this song --------------------------------------------- */

  function takesBlock(): HTMLElement {
    const sec = h('section', { class: 'wb-card' }, [h('h2', { text: 'Takes' })]);

    if (state.takes.length === 0) {
      sec.appendChild(h('p', { class: 'wb-none', text: 'Nothing recorded for this one yet.' }));
      return sec;
    }

    for (const t of state.takes) sec.appendChild(takeRow(t));
    return sec;
  }

  function takeRow(t: Take): HTMLElement {
    const k = kindOf(t.kind);
    const guessed = t.auto_confidence != null && !t.confirmed_by;

    const menu = h('button', {
      class: 'wb-x',
      'aria-label': 'What to do with this take',
      html: icon.more,
    });
    menu.addEventListener('click', (e) => {
      e.stopPropagation();
      takeMenu(t);
    });

    const row = h('div', { class: 'wb-take' }, [
      h('span', { class: 'wb-dot', style: `background:${k.color}` }),
      h('div', { class: 'wb-take-main' }, [
        h('strong', { text: t.label || t.filename || 'Untitled take' }),
        h('span', {
          class: 'wb-sub',
          text: [
            fmtTime(t.duration_s),
            t.session_label || (t.recorded_at ? relDate(t.recorded_at) : ''),
            guessed ? `guessed · ${pct(t.auto_confidence!)}` : '',
            !t.playable ? 'waiting on your Mac' : '',
            t.playable && !t.has_peaks ? 'no waveform yet' : '',
          ]
            .filter(Boolean)
            .join(' · '),
        }),
      ]),
      menu,
    ]);

    row.addEventListener('click', () => void openTake(t, t.has_peaks ? 'split' : 'confirm'));
    return row;
  }

  function takeMenu(t: Take): void {
    const items: SheetItem[] = [];

    if (t.has_peaks) {
      items.push({
        label: 'Cut it into songs',
        icon: icon.more,
        onSelect: () => void openTake(t, 'split'),
      });
      items.push({
        label: 'Trim the ends off',
        icon: icon.pencil,
        onSelect: () => void openTake(t, 'trim'),
      });
    }

    items.push({
      label: 'Save this as a version',
      icon: icon.star,
      accent: true,
      onSelect: () => void doPromote(t),
    });
    items.push({ label: 'Rename it', icon: icon.pencil, onSelect: () => void doRenameTake(t) });
    items.push({
      label: 'Actually, it’s a different song',
      icon: icon.shelf,
      onSelect: () => void openTake(t, 'confirm'),
    });

    sheet(t.label || t.filename || 'This take', items);
  }

  /* ---- the pile nobody has named ------------------------------------------ */

  function unsortedBlock(): HTMLElement {
    const sec = h('section', { class: 'wb-card' }, [
      h('h2', { text: 'Not sure which song these are' }),
      h('p', {
        class: 'wb-teach',
        text: 'The matcher put its best guesses at the top. One tap tells it whether it was right — and that is what makes tomorrow’s guesses better.',
      }),
    ]);

    if (state.unsorted.length === 0) {
      sec.appendChild(h('p', { class: 'wb-none', text: 'Nothing waiting. Rare and lovely.' }));
      return sec;
    }

    for (const t of state.unsorted.slice(0, 12)) {
      const scored = t.score != null;

      const row = h('div', { class: 'wb-take', 'data-hot': scored ? '1' : null }, [
        h('span', { class: 'wb-dot', style: 'background:#555' }),
        h('div', { class: 'wb-take-main' }, [
          h('strong', { text: t.label || t.filename || 'Untitled take' }),
          h('span', {
            class: 'wb-sub',
            text: [
              fmtTime(t.duration_s),
              t.session_label || (t.recorded_at ? relDate(t.recorded_at) : ''),
            ]
              .filter(Boolean)
              .join(' · '),
          }),
        ]),
        scored
          ? h('span', { class: 'wb-score', text: pct(t.score!), title: 'how sure the matcher is' })
          : null,
      ]);

      row.addEventListener('click', () => void openTake(t, 'confirm'));
      sec.appendChild(row);
    }

    return sec;
  }

  /* ---- the editor --------------------------------------------------------- */

  function editor(): HTMLElement {
    const take = state.take!;
    const wrap = h('div', { class: 'wb-editor' });

    if (state.loading) {
      wrap.appendChild(skeleton(1));
      return wrap;
    }

    if (state.mode === 'confirm') {
      wrap.appendChild(confirmPanel(take));
      return wrap;
    }

    // No peaks = no waveform = nothing to cut against. Say it plainly, offer
    // the things that DON'T need a waveform, and never show a dead grey box.
    if (!state.peaks) {
      wrap.appendChild(
        emptyState(
          'No waveform for this one yet',
          'Your Mac draws the waveform. Until it has, there’s nothing to cut against — but you can still say which song it is, or save it as a version.',
        ),
      );
      wrap.appendChild(confirmPanel(take));
      return wrap;
    }

    wrap.appendChild(waveWrap);
    wrap.appendChild(transport());

    if (state.mode === 'split') {
      wrap.appendChild(
        h('p', {
          class: 'wb-teach',
          text: 'These boxes are a guess, made from the quiet bits. Drag them until they’re right, tap one to hear just it, then cut once.',
        }),
      );
      wrap.appendChild(cutList());
      wrap.appendChild(splitActions(take));
    }

    if (state.mode === 'trim') {
      wrap.appendChild(
        h('p', {
          class: 'wb-teach',
          text: 'Drag the box around the bit worth keeping. This makes a NEW take — the original stays exactly as it is, forever.',
        }),
      );
      wrap.appendChild(trimActions(take));
    }

    return wrap;
  }

  function transport(): HTMLElement {
    const play = h('button', {
      class: 'wb-play',
      'aria-label': 'Play',
      html: ws?.isPlaying() ? icon.pause : icon.play,
    });
    play.addEventListener('click', () => void ws?.playPause());

    const zoom = h('input', {
      type: 'range',
      class: 'wb-zoom',
      min: '1',
      max: '60',
      value: '1',
      'aria-label': 'Zoom in on the waveform',
    });
    zoom.addEventListener('input', () => {
      try {
        ws?.zoom(Number(zoom.value));
      } catch {
        /* zooming before 'ready' throws. Harmless: they'll drag it again. */
      }
    });

    return h('div', { class: 'wb-transport' }, [play, zoom]);
  }

  function cutList(): HTMLElement {
    const list = h('div', { class: 'wb-cuts' });
    const ordered = [...state.cuts].sort((a, b) => a.region.start - b.region.start);

    for (const cut of ordered) {
      const k = kindOf(cut.kind);
      const song = state.songs.find((s) => s.id === cut.songId);

      const hear = h('button', { class: 'wb-x hear', 'aria-label': 'Hear just this bit', html: icon.play });
      const drop = h('button', { class: 'wb-x danger', 'aria-label': 'Drop this bit', text: '×' });
      const kindBtn = h('button', { class: 'chip cut-kind', text: k.label });
      const songBtn = h('button', { class: 'chip cut-song', text: song ? song.title : 'Which song?' });

      hear.addEventListener('click', () => cut.region.play(true));
      drop.addEventListener('click', () => dropCut(cut));
      kindBtn.addEventListener('click', () => pickKindFor(cut));
      songBtn.addEventListener('click', () => pickSongFor(cut));

      list.appendChild(
        h(
          'div',
          {
            class: 'wb-cut',
            'data-cut': cut.id,
            'data-unnamed': song ? '0' : '1',
            style: `--k:${k.color}`,
          },
          [
            hear,
            h('div', { class: 'cut-main' }, [
              h('div', { class: 'cut-line' }, [
                h('span', {
                  class: 'cut-time',
                  text: `${fmtTime(cut.region.start)} – ${fmtTime(cut.region.end)}`,
                }),
                h('span', { class: 'cut-dur', text: fmtTime(cut.region.end - cut.region.start) }),
              ]),
              h('div', { class: 'cut-line' }, [kindBtn, songBtn]),
            ]),
            drop,
          ],
        ),
      );
    }

    const add = h('button', { class: 'wb-btn ghost', text: '+ Another bit' });
    add.addEventListener('click', () => {
      const last = ordered[ordered.length - 1];
      const from = last ? last.region.end : 0;
      addCut(from, Math.min(state.duration, from + 60), 'full');
      render();
    });
    list.appendChild(add);

    return list;
  }

  function splitActions(take: Take): HTMLElement {
    const n = state.cuts.length;

    const go = h('button', {
      class: 'wb-go',
      text: state.busy ? 'Cutting…' : `Cut into ${n} ${n === 1 ? 'piece' : 'pieces'}`,
      disabled: state.busy || n === 0,
    });
    go.addEventListener('click', doSplit);

    return h('div', { class: 'wb-actions' }, [
      go,
      h('p', {
        class: 'wb-fine',
        // Say what actually happens. It's reassuring, and it's true.
        text: 'This sends the numbers only — no audio leaves this phone. Your Mac makes the cuts with a stream copy: instant, and not one sample is re-encoded.',
      }),
      h('p', {
        class: 'wb-fine',
        text: `“${take.label || take.filename || 'The original'}” stays exactly where it is.`,
      }),
    ]);
  }

  function trimActions(take: Take): HTMLElement {
    const cut = state.cuts[0];

    const go = h('button', {
      class: 'wb-go',
      text: state.busy ? 'Trimming…' : 'Keep this bit',
      disabled: state.busy || !cut,
    });
    go.addEventListener('click', doTrim);

    return h('div', { class: 'wb-actions' }, [
      go,
      cut
        ? h('p', {
            class: 'wb-fine',
            text: `Keeping ${fmtTime(cut.region.start)} – ${fmtTime(cut.region.end)} of “${
              take.label || take.filename || 'this take'
            }” as a new take.`,
          })
        : null,
    ]);
  }

  /* ---- confirm: the most valuable tap in the app -------------------------- */

  function confirmPanel(take: Take): HTMLElement {
    const panel = h('section', { class: 'wb-card' }, [h('h2', { text: 'Which song is this?' })]);

    if (state.candidates.length === 0) {
      panel.appendChild(
        h('p', {
          class: 'wb-none',
          text: 'The matcher hasn’t had a look at this one yet. Pick it by hand:',
        }),
      );
    }

    for (const c of state.candidates) {
      const btn = h('button', { class: 'wb-cand', style: `--k:${c.color ?? '#ff3333'}` }, [
        h('div', { class: 'cand-top' }, [
          h('strong', { text: c.title }),
          h('span', { class: 'cand-pct', text: pct(c.score) }),
        ]),
        h('div', { class: 'cand-bar' }, [h('i', { style: `width:${Math.round(c.score * 100)}%` })]),
        h('span', { class: 'cand-why', text: why(c) }),
      ]);
      btn.addEventListener('click', () => doConfirm(c.song_id));
      panel.appendChild(btn);
    }

    const other = h('button', { class: 'wb-btn ghost', text: 'None of these — I’ll pick it' });
    other.addEventListener('click', () => {
      sheet('Which song is this?', [
        ...state.songs.map((s) => ({ label: s.title, onSelect: () => doConfirm(s.id) })),
      ]);
    });
    panel.appendChild(other);

    /* ═══ THE ENGINE OF THE WHOLE MATCHING SYSTEM (ADR-008) ═══
     *
     * A confirmed take joins the reference bank, and new jams are matched
     * against the REFERENCE BANK — same room, same amps, same arrangement —
     * not against a studio master they share no spectral landmarks with.
     * (They can't be. Fingerprinting cannot match take 3 to take 11, ever.
     * That's why this button exists and an "auto-tag everything" button does
     * not.)
     *
     * Cold start is 60-75% top-1. After a few confirmations per song: 85-95%.
     * The accuracy of this app is quite literally a function of how many times
     * somebody has tapped one of those buttons.
     *
     * So the buttons are big, and the screen says out loud what the tap is
     * worth. That sentence below is not decoration — it is the reason anyone
     * bothers to tap it twice. */
    panel.appendChild(
      h('p', {
        class: 'wb-teach',
        text: 'Every time you answer this, the matcher gets better at guessing the next one. It learns from your jams — not from a studio recording it has never heard.',
      }),
    );

    if (take.song_id) {
      const promote = h('button', { class: 'wb-btn', text: 'Save this as a version instead' });
      promote.addEventListener('click', () => void doPromote(take));
      panel.appendChild(promote);
    }

    return panel;
  }

  /** What the matcher actually noticed, in English. Never a number nobody asked for. */
  function why(c: Candidate): string {
    const NAMES: Record<string, string> = {
      qmax: 'the tune',
      tempo: 'the tempo',
      chords: 'the chords',
      lyrics: 'the words',
      neighbour: 'a take you already confirmed',
      olaf: 'the same recording',
    };

    const strong = Object.entries(c.evidence)
      .filter(([, v]) => typeof v === 'number' && v >= 0.6)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([k]) => NAMES[k] ?? k);

    return strong.length === 0 ? 'a weak guess' : `${strong.join(' and ')} match`;
  }

  /* ------------------------------------------------------------------ start */

  void loadSong();

  /* --------------------------------------------------------------- teardown */

  return () => {
    dead = true;
    abort.abort();
    teardownWave();

    // Hand the <audio> element back exactly as we found it: silent, no src.
    // The next tap on a record owns it again.
    try {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    } catch {
      /* nothing left to clean up */
    }

    view.remove();
  };
}

/* -------------------------------------------------------------------- maths */

const round = (n: number) => Math.round(n * 100) / 100;
const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * The peaks array as an amplitude envelope: one value per pixel.
 *
 * `audiowaveform` gives interleaved min,max pairs. The loudness of a pixel is
 * the larger of the two magnitudes. This is arithmetic on numbers already sat
 * in memory — nothing is decoded, nothing is fetched, and it runs in
 * single-digit milliseconds on a 20-minute file.
 */
function envelope(peaks: number[][]): number[] {
  const ch = peaks[0] ?? [];
  const out: number[] = [];
  for (let i = 0; i < ch.length; i += 2) {
    out.push(Math.max(Math.abs(ch[i] ?? 0), Math.abs(ch[i + 1] ?? 0)));
  }
  return out;
}

/**
 * Quiet is relative. A room mic on a Zoom H4n is not the same silence as a DI'd
 * guitar, so the threshold is a fraction of what THIS recording thinks loud is
 * (its 90th percentile) — never an absolute dB, which would work on one file
 * and be laughably wrong on the next.
 */
function threshold(env: number[]): number {
  const sorted = [...env].sort((a, b) => a - b);
  const loud = sorted[Math.floor(sorted.length * 0.9)] ?? 1;
  return Math.max(loud * 0.08, 0.012);
}

/** The loud bit of the file: where sound starts, where it stops. */
function sounding(peaks: number[][], duration: number): { start: number; end: number } | null {
  const env = envelope(peaks);
  if (env.length === 0) return null;

  const t = threshold(env);
  const spp = duration / env.length;

  const first = env.findIndex((v) => v > t);
  if (first < 0) return null;

  let last = -1;
  for (let i = env.length - 1; i >= 0; i--) {
    if ((env[i] ?? 0) > t) {
      last = i;
      break;
    }
  }
  if (last < 0) return null;

  // Half a second of air either side. A cut that clips the first snare is a cut
  // you have to do again.
  const pad = Math.ceil(0.5 / spp);
  const from = Math.max(0, first - pad);
  const to = Math.min(env.length - 1, last + pad);

  return { start: from * spp, end: Math.min(duration, (to + 1) * spp) };
}

interface Boundary {
  start: number;
  end: number;
  kind: TakeKind;
}

/** Gaps of quiet → boundaries → pieces. A first guess, and only ever that. */
function boundaries(peaks: number[][], duration: number): Boundary[] {
  const env = envelope(peaks);
  if (env.length < 8 || !duration) return [];

  const t = threshold(env);
  const spp = duration / env.length;

  const MIN_GAP_S = 2.5; // a pause this long, in a room with five people in it, is a gap between songs
  const MIN_PIECE_S = 8; // shorter than this is a cough, not a take
  const MIN_SONG_S = 75; // a proper run at a song. Anything less is a fragment.
  const MAX_PIECES = 12; // a jam with more parts than this needs a human, not a heuristic

  const gapPixels = Math.max(1, Math.ceil(MIN_GAP_S / spp));

  const pieces: { from: number; to: number }[] = [];
  let runStart = -1;
  let quiet = 0;

  for (let i = 0; i < env.length; i++) {
    if ((env[i] ?? 0) > t) {
      if (runStart < 0) runStart = i;
      quiet = 0;
    } else if (runStart >= 0) {
      quiet++;
      if (quiet >= gapPixels) {
        pieces.push({ from: runStart, to: i - quiet });
        runStart = -1;
        quiet = 0;
      }
    }
  }
  if (runStart >= 0) pieces.push({ from: runStart, to: env.length - 1 });

  const pad = Math.ceil(0.4 / spp);

  return pieces
    .map(({ from, to }) => {
      const start = Math.max(0, (from - pad) * spp);
      const end = Math.min(duration, (to + pad + 1) * spp);
      const len = end - start;
      return { start, end, len, kind: (len >= MIN_SONG_S ? 'full' : 'fragment') as TakeKind };
    })
    .filter((p) => p.len >= MIN_PIECE_S)
    .sort((a, b) => b.len - a.len)
    .slice(0, MAX_PIECES) // keep the longest; twelve boxes on a screen is already a lot
    .sort((a, b) => a.start - b.start)
    .map(({ start, end, kind }) => ({ start, end, kind }));
}

/* ---------------------------------------------------------------------- css */

/**
 * The workbench brings its own stylesheet.
 *
 * Not because styles.css is wrong, but because this is a self-contained admin
 * door that mounts on demand — and its ~120 rules are dead weight in the
 * critical path of the SHELF, which is the screen a band member on a bad
 * connection in a pub is actually waiting for. It inherits every variable from
 * styles.css, so it stays in the design language for free.
 */
let cssDone = false;

function injectCss(): void {
  if (cssDone || document.getElementById('wb-css')) return;
  cssDone = true;

  const style = document.createElement('style');
  style.id = 'wb-css';
  style.textContent = `
.wb { padding: 0 16px 96px; }

.wb-head { display: flex; align-items: center; gap: 12px; padding: 12px 0 18px; }
.wb-title h1 { font: 700 19px/1.2 'Courier New', monospace; letter-spacing: .02em; margin: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wb-title p { margin: 3px 0 0; font-size: 12px; color: var(--text2); }
.wb-busy { width: 16px; height: 16px; margin-left: auto; flex: none; border-radius: 50%;
  border: 2px solid var(--glass); border-top-color: var(--accent);
  animation: wb-spin .8s linear infinite; }
@keyframes wb-spin { to { transform: rotate(360deg); } }

.wb-x { display: grid; place-items: center; min-width: 44px; min-height: 44px; flex: none;
  border: 0; background: none; color: var(--text); cursor: pointer; border-radius: 12px; }
.wb-x:active { background: var(--glass); }
.wb-x svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 2;
  stroke-linecap: round; stroke-linejoin: round; }
.wb-x.hear svg { fill: currentColor; stroke: none; }
.wb-x.danger { color: var(--text2); font-size: 24px; line-height: 1; }

.wb-card { background: var(--glass); border: 1px solid rgba(255,255,255,.07);
  border-radius: var(--radius); padding: 16px; margin-bottom: 16px; }
.wb-card h2 { font: 700 12px/1 'Courier New', monospace; letter-spacing: .12em;
  text-transform: uppercase; color: var(--text2); margin: 0 0 14px; }

.wb-facts { display: flex; gap: 18px; flex-wrap: wrap; margin-bottom: 14px; }
.wb-fact { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.wb-fact span { font-size: 11px; color: var(--text2); text-transform: uppercase; letter-spacing: .08em; }
.wb-fact strong { font-size: 15px; }

.wb-btn { display: flex; align-items: center; gap: 10px; width: 100%; min-height: 48px;
  padding: 0 14px; margin-top: 8px; border: 1px solid rgba(255,255,255,.09);
  border-radius: 12px; background: var(--bg2); color: var(--text);
  font-size: 15px; text-align: left; cursor: pointer; }
.wb-btn:active { background: var(--bg3); }
.wb-btn.ghost { background: none; border-style: dashed; color: var(--text2); justify-content: center; }
.wb-btn .lead { display: grid; place-items: center; width: 20px; height: 20px; flex: none; color: var(--text2); }
.wb-btn .lead svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2;
  stroke-linecap: round; stroke-linejoin: round; }

.wb-take { display: flex; align-items: center; gap: 10px; min-height: 56px;
  padding: 8px 0; border-top: 1px solid rgba(255,255,255,.06); cursor: pointer; }
.wb-take:first-of-type { border-top: 0; }
.wb-take[data-hot='1'] { background: linear-gradient(90deg, rgba(255,51,51,.07), transparent 60%); }
.wb-take-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.wb-take-main strong { font-size: 15px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wb-sub { font-size: 12px; color: var(--text2); }
.wb-dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.wb-score { font: 700 13px 'Courier New', monospace; color: var(--accent); }
.wb-none { color: var(--text2); font-size: 14px; margin: 0; }
.wb-teach { font-size: 13px; line-height: 1.5; color: var(--text2); margin: 12px 0 0; }
.wb-fine { font-size: 12px; line-height: 1.5; color: var(--text2); margin: 10px 0 0; }

.wb-wave-wrap { background: var(--bg2); border-radius: var(--radius); padding: 10px;
  margin-bottom: 12px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.wb-wave { min-height: 96px; }

.wb-transport { display: flex; align-items: center; gap: 14px; }
.wb-play { display: grid; place-items: center; width: 52px; height: 52px; flex: none;
  border: 0; border-radius: 50%; background: var(--accent); color: #fff; cursor: pointer; }
.wb-play svg { width: 22px; height: 22px; fill: currentColor; stroke: none; }
.wb-zoom { flex: 1; height: 44px; accent-color: var(--accent); background: none; }

.wb-cuts { margin-top: 12px; }
.wb-cut { display: flex; align-items: center; gap: 8px; padding: 8px 0;
  border-top: 1px solid rgba(255,255,255,.06); }
.wb-cut:first-child { border-top: 0; }
.wb-cut .cut-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.cut-line { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cut-time { font: 700 13px 'Courier New', monospace; color: var(--k); }
.cut-dur { font-size: 12px; color: var(--text2); }
.chip { min-height: 36px; padding: 0 12px; border-radius: 999px; cursor: pointer;
  border: 1px solid rgba(255,255,255,.12); background: var(--bg2); color: var(--text);
  font-size: 13px; white-space: nowrap; max-width: 46vw;
  overflow: hidden; text-overflow: ellipsis; }
.wb-cut[data-unnamed='1'] .cut-song { border-color: var(--accent); color: var(--accent); }

.wb-actions { margin-top: 16px; }
.wb-go { width: 100%; height: 64px; border: 0; border-radius: var(--radius);
  background: var(--accent); color: #fff; cursor: pointer;
  font: 700 16px/1 'Courier New', monospace; letter-spacing: .04em; }
.wb-go:disabled { opacity: .45; }

.wb-cand { display: block; width: 100%; text-align: left; cursor: pointer;
  padding: 14px; margin-bottom: 10px; border-radius: 14px;
  border: 1px solid rgba(255,255,255,.1); background: var(--bg2); color: var(--text); }
.wb-cand:active { border-color: var(--k); }
.cand-top { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
.cand-top strong { font-size: 16px; }
.cand-pct { font: 700 14px 'Courier New', monospace; color: var(--k); }
.cand-bar { height: 6px; margin: 8px 0 6px; border-radius: 999px;
  background: rgba(255,255,255,.08); overflow: hidden; }
.cand-bar i { display: block; height: 100%; border-radius: 999px; background: var(--k); }
.cand-why { font-size: 12px; color: var(--text2); }

/* wavesurfer draws into a shadow root; the region boxes are ours to style. */
.wb-wave ::part(region) { border-radius: 4px; }
.wb-wave ::part(region-handle) { border-width: 3px; }
`;
  document.head.appendChild(style);
}
